// src/components/ErrorBoundary.js

import React from 'react';
import { XMarkIcon, ExclamationTriangleIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error: error.message };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorDialog error={this.state.error} onClose={() => this.setState({ hasError: false })} />;
    }

    return this.props.children;
  }
}

export const ErrorDialog = ({ error, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
        className="bg-white p-4 rounded-lg shadow-lg max-w-md w-full m-4"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mr-2" />
            <h3 className="text-md font-bold text-gray-700">Error Occurred</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        
        <p className="mb-4 text-sm text-gray-600">{error}</p>
        
        <div className="flex flex-wrap gap-2">
          {error && error.includes('Insufficient credits') && (
            <a 
              href="https://apilabz.com/profile"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium py-2 px-3 rounded inline-flex items-center"
            >
              Add Credits
              <ArrowTopRightOnSquareIcon className="ml-1 h-3 w-3" />
            </a>
          )}
          
          <a 
            href="https://apilabz.com/api-labs/dashboard"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2 px-3 rounded inline-flex items-center"
          >
            Track Activity
            <ArrowTopRightOnSquareIcon className="ml-1 h-3 w-3" />
          </a>
          
          <button 
            onClick={onClose}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2 px-3 rounded"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ErrorBoundary;